// /large-scale-3d-printing-service/ (PrintingProcessPage.js). Code-owned since
// 2026-11-04; gatsby-node skips the Contentful entry.
// GSC Aug–Oct 2026: 428 clicks. Top queries kept in the title, H1 and H2s:
// "large scale 3d printing (service)", "large 3d printing", "large 3d prints",
// "large format 3d printing service", "3d printing large objects".
// Facts: prints of 6 ft and more in sections (truth.md large-format rows),
// standard 1–2 weeks and 1–2 months for complex builds, the owner's prop prices
// and anonymized prop projects (truth.md "Agency and event pages"), the
// large-format SLA platform of 600 × 600 mm (biggest-printers-guide.js).
// Owner direction 2026-11-03: keep the engineer and artist audience and add
// brands and agencies, linking to /services/custom-props/.
const {
  pricing,
  photoRoot,
  reviewedDate,
  studios,
  delivery,
  files,
  noFile,
  hobby,
  quoteSteps,
  handoffStep,
} = require('./shared')

const page = {
  path: '/large-scale-3d-printing-service/',
  crumb: 'Large-scale 3D printing',
  title: 'Large Scale 3D Printing Service | Large 3D Prints 6 ft+',
  description:
    'Large scale 3D printing for brands, agencies, artists and architects: large-format prints joined to 6 ft and more, painted or plated, shipped to all 50 states.',
  heading: 'Large Scale 3D Printing Service',
  eyebrow: 'Large-format 3D printing · Large 3D prints, 6 ft and more',
  answer:
    'PrintAWorld is a large scale 3D printing service in New York for sculptures, props, giant product replicas, architectural models and prototypes. We print large objects in sections on our large-format resin printers and in-house FDM print farm, join them, then sand, paint or metal plate them. Finished pieces reach 6 feet and more. Standard large-format builds take 1–2 weeks, there’s no setup fee, and we ship to all 50 states.',
  hero: 'submarine',
  faqHeading: 'What do people ask about large 3D printing?',
  quoteText:
    'Send the file or photos, the finished size, and where the piece is going. We’ll reply with a price, how we’d section it, and a schedule.',
  reviewedDate,
}

const card = {
  label: 'Big builds',
  title: 'Large-format 3D printing',
  body: 'Sculptures, props and models printed in sections, 6 feet and more.',
}

const photos = {
  window: {
    url: `${photoRoot}/3E9CrRQJNhzgq4QTz0r5c2/b896cb34d6471434b1fabcda763151df/tiffany-co-display-3d-printed-and-metal-plated.jpg`,
    width: 2518,
    height: 1500,
    alt: 'A store window display with a large 3D printed figure finished in metal plating',
    title: 'Plated window display',
    caption: 'A store window centerpiece, 3D printed and metal plated.',
  },
  austinLee: {
    url: `${photoRoot}/33eadFRtkpbMjbkgLPcLWR/e05fe6fb96f4929ee40bfdc9f739bf59/L1030812.jpg`,
    width: 3333,
    height: 5000,
    alt: 'A 6-foot painted sculpture of a running figure in a gallery',
    title: '6-foot sculpture for Austin Lee',
    caption:
      'Printed in sections, joined and painted for the artist Austin Lee.',
  },
  tinMan: {
    url: `${photoRoot}/7E2irwmYl2aLVRFMSmoxdk/51352400686628e266bc28d65ac41703/IMG_7996.jpg`,
    width: 4032,
    height: 3024,
    alt: 'A standing metallic Tin Man sculpture on a white plinth in a gallery',
    title: 'Tin Man sculpture for Coco Fusco',
    caption:
      'A standing figure for the artist Coco Fusco, with a metal finish.',
  },
  submarine: {
    url: `${photoRoot}/6iDyghZXUx8SSbZlQcp4DH/af03888d253a96287ecaef51e5e64c29/_DSC0393.jpg`,
    width: 3000,
    height: 2002,
    alt: 'A large painted display model of a deep-sea submersible on a stand',
    title: 'Submersible display model',
    caption: 'A long display model, printed in parts and painted in detail.',
  },
  tower: {
    url: `${photoRoot}/uvqKcou69EGg2ySAc88gO/8d0fc7f714b7a59eeb35a92cfad9fd59/3d-printed-architectural-model-min.jpg`,
    width: 1335,
    height: 2000,
    alt: 'A white 3D printed architectural model of a residential tower',
    title: '2-foot tower model',
    caption: 'An architectural model of a tower, about 2 feet tall.',
  },
  clocks: {
    url: `${photoRoot}/5lBP81FqLzeNsr2XZGtOhj/c656ea124d1f695eb471343c6743d9c3/LAL_4244.jpg`,
    width: 4685,
    height: 6837,
    alt: 'Two Atlas clock statues, one with a bronze patina finish and one raw white 3D print',
    title: 'Atlas statues, raw and finished',
    caption: 'A raw print beside the same statue finished in bronze patina.',
  },
  console: {
    url: `${photoRoot}/398QhqbtWCn7iFJ2ni5DAh/5b9143309b4a1cf209b110babafa8d26/BC9A4827-Edit.jpg`,
    width: 720,
    height: 1080,
    alt: 'A sculptural wall-mounted console with a metal finish',
    title: 'Sculptural wall console',
    caption: 'A sculptural furniture piece with a metal finish.',
  },
  printers: {
    url: `${photoRoot}/16zgIkjEpkbS4fSqzGirY1/760694e1be95dd5ed538eb71f35a3f5e/L1030539.jpg`,
    width: 1200,
    height: 800,
    alt: 'Two UnionTech Lite600HD industrial resin 3D printers in the PrintAWorld studio',
    caption: 'Two of our UnionTech Lite600HD large-format resin printers.',
  },
}

const intro = {
  eyebrow: 'Big 3D prints',
  heading: 'What is a large scale 3D printing service?',
  paragraphs: [
    'A large scale 3D printing service makes objects too big for a desktop printer: life-size sculptures, giant props, building models, full-size prototypes. Most printers, ours included, have a build area under a meter, so large objects are printed in sections that are pinned, glued and filled until the seams disappear.',
    'We do the whole job in our New York studios: we check and section the file, print it, assemble it, and sand, paint or plate it. We work for brands, agencies, artists, architects and engineers, from a single 6-foot showpiece to a run of large parts on our FDM print farm.',
  ],
}

const glance = {
  heading: 'Our large-format 3D printing service at a glance',
  rows: [
    ['Studios', studios],
    ['Delivery', delivery],
    ['Largest size', '6 feet and more, printed in sections and joined'],
    [
      'Printers',
      'Large-format SLA resin printers with a 600 × 600 mm (23.6 × 23.6 in) build platform, plus our in-house FDM print farm',
    ],
    [
      'Finishing',
      'Seam filling, sanding, priming, painting and metal plating (chrome, gold, bronze looks)',
    ],
    [
      'Who it’s for',
      'Brands, agencies, artists, architects, engineers and production teams',
    ],
    [
      'Props price',
      'Small props from about $1,000; 12–24 inch hero props $1,500–$3,500; 5-foot showpieces around $10,000',
    ],
    ['Other large builds', 'Quoted per project. No setup fee.'],
    [
      'Turnaround',
      'Standard 1–2 weeks; bigger, complex builds 1–2 months. Rush jobs can start the day the invoice is paid.',
    ],
    ['Files', files],
    ['No file?', noFile],
  ],
}

const work = {
  heading: 'Large 3D prints from our studio.',
  photos: ['austinLee', 'tinMan', 'window', 'tower', 'clocks', 'console'],
}

const before = [
  {
    id: 'sectioning',
    eyebrow: 'How it’s done',
    heading: 'How do you 3D print large objects?',
    index: '02 / THE METHOD',
    paragraphs: [
      'By splitting them. We cut the model into sections that fit the printer, add pins or keys so the parts line up, print them, then bond and fill the joints. After sanding and primer the seams don’t show, and the piece can be painted or plated as one surface.',
      'Big pieces are often printed hollow, with a wall thick enough to hold their shape, so they stay light enough to move. If a piece has to stand in a public space, travel to events or be taken apart, tell us; it changes how we section and reinforce it.',
    ],
    list: [
      'One print: up to the 600 × 600 mm platform of our large-format resin printers',
      'Finished piece: 6 feet and more, assembled from sections',
      'Large runs: the same part, many times, on our FDM print farm',
    ],
    photo: 'printers',
    link: {
      to: '/guides/biggest-3d-printers/',
      label: 'The biggest 3D printers you can buy, and when to outsource',
    },
  },
  {
    id: 'brands',
    eyebrow: 'For brands and agencies',
    heading:
      'For brands and agencies: activation props, giant product replicas and booth centerpieces',
    index: '03 / THE PROPS',
    paragraphs: [
      'Large-format printing is how we make giant product replicas, photo-op pieces and trade show centerpieces for brands, experiential agencies and PR teams: oversized bottles, cans, tubes and packaging, printed in sections, joined, then painted or metal plated. Small props start around $1,000; 12–24 inch hero props usually cost $1,500–$3,500; 5-foot showpieces are around $10,000, quoted per project.',
    ],
    list: [
      'Three 5-foot personal-care product replicas for a global brand’s activation',
      'Giant donut props, 12 inches and life size, for a financial brand’s event',
      'A 57-inch roller-coaster display model for an amusement park',
      'Oversized skincare tubs used as drinking vessels, with food-safe inserts',
    ],
    link: {
      to: '/services/custom-props/',
      label: 'Custom props and giant product replicas',
    },
  },
  {
    id: 'uses',
    eyebrow: 'What it’s for',
    heading: 'What can large-format 3D printing make?',
    index: '04 / THE USES',
    cards: [
      {
        label: 'U1',
        title: 'Sculptures and art',
        body: 'Life-size and larger sculptures for artists and galleries, finished in paint, patina or metal plating.',
      },
      {
        label: 'U2',
        title: 'Props, displays and windows',
        body: 'Event props, store windows, booth centerpieces and giant product replicas for brands and agencies.',
      },
      {
        label: 'U3',
        title: 'Architectural models',
        body: [
          'Large building, site and interior models, printed in sections and assembled. See ',
          {
            to: '/services/architectural-3d-printing/',
            label: 'architectural 3D printing',
          },
          '.',
        ],
      },
      {
        label: 'U4',
        title: 'Full-size prototypes',
        body: 'Product, furniture and vehicle-part prototypes at real size, to check proportion and fit.',
      },
    ],
  },
]

const contrast = {
  id: 'no-file',
  eyebrow: 'No 3D file?',
  heading: 'Can you make a large 3D print from a sketch or a photo?',
  paragraphs: [
    'Yes. Most giant props start as a product photo, a render or a sketch. We model it at full size, send it for your approval, then section and print it.',
    `3D modeling starts at $${pricing.modelingFrom}. To copy a real object at a bigger size, 3D scanning starts at $${pricing.scanningFrom}.`,
  ],
  links: [
    { to: '/services/3d-modeling/', label: 'Explore 3D modeling' },
    { to: '/services/3d-print-from-photo/', label: '3D print from a photo' },
  ],
}

const fit = {
  eyebrow: 'Is it a fit?',
  heading: 'Which large 3D printing service is right for you?',
  index: '05 / THE FIT',
  good: [
    'Brand activations, trade show booths, pop-ups and store windows',
    'Sculptures and art pieces of 6 feet and more',
    'Large architectural and site models',
    'Pieces that need sanding, paint or metal plating, not just raw prints',
    'Runs of large parts on our in-house FDM print farm',
    'Deadlines: rush jobs can start the day the invoice is paid',
  ],
  other: [
    'A single raw part that fits one large printer, with no finishing: an instant-quote website can be cheaper.',
    'Structural, load-bearing or outdoor building parts: a construction-scale printer or fabricator suits those better.',
    hobby,
  ],
}

const pricingBlock = {
  heading: 'How much does large scale 3D printing cost?',
  index: '06 / THE PRICE',
  lead: {
    label: 'Large props start around',
    value: '$1,000',
    note: 'For a small finished prop. Smaller raw prints start around $30.',
  },
  rows: [
    ['Small prop, finished', 'From about $1,000'],
    ['Hero prop, 12–24 inches', '$1,500–$3,500'],
    ['5-foot showpiece', 'Around $10,000'],
    ['Sculptures, models and other large builds', 'Quoted per project'],
    ['3D modeling, if there’s no file', `From $${pricing.modelingFrom}`],
    ['Setup fee', 'None'],
    ['Shipping, next-day available', 'Quoted to your ZIP'],
  ],
  note: 'For planning only. Size, wall thickness, number of sections, finish, quantity and deadline set the final price, and your written quote confirms it.',
}

const steps = {
  heading: 'How our large-format 3D printing service works',
  index: '07 / THE PROCESS',
  items: [
    ...quoteSteps,
    {
      title: 'We section and print',
      body: 'We split the model into printable sections, print them in our New York studios and remove supports.',
    },
    {
      title: 'Assemble and finish',
      body: 'Sections are joined, filled and sanded, then primed, painted or plated, as quoted.',
    },
    handoffStep,
  ],
}

const checklist = [
  'Your 3D file (STL, OBJ, STEP, STP or 3DM), or photos, renders or a sketch',
  'The finished size: height, width and depth',
  'How many you need',
  'Paint colors, plating or finish',
  'Where it will stand: indoors, outdoors, handled, touring',
  'Your delivery ZIP code, and the load-in or event date',
]

const faqs = [
  {
    question: 'How big can you 3D print?',
    answer:
      'Finished pieces of 6 feet and more. One print is limited by the printer, so we print large objects in sections and join them. Our large-format resin printers have a 600 × 600 mm (23.6 × 23.6 in) build platform; anything bigger is sectioned, assembled and finished so the seams don’t show.',
  },
  {
    question: 'How much does a large 3D print cost?',
    answer: `For props, small finished pieces start around $1,000; 12–24 inch hero props usually cost $1,500–$3,500; 5-foot showpieces are around $10,000. Sculptures, models and other large builds are quoted per project. Simple prints start around $${pricing.printFrom}, and there’s no setup fee.`,
  },
  {
    question: 'How long does large-format 3D printing take?',
    answer:
      'Standard large-format builds take 1–2 weeks; bigger, complex projects can take 1–2 months. On a rush we can start the day the invoice is paid; a painted 24-inch bust took 2 days. Your written quote confirms the date, so send your deadline with the file.',
  },
  {
    question: 'Can you make giant product replicas for a brand activation?',
    answer:
      'Yes. We 3D print oversized bottles, cans, tubes and packaging for activations, pop-ups, trade show booths and store windows, printed in sections and painted or metal plated. We made three 5-foot personal-care product replicas for a global brand’s activation.',
  },
  {
    question: 'Can you print a large object in one piece?',
    answer:
      'Only up to the printer’s build area. Anything larger is printed in sections, keyed so the parts line up, then bonded, filled and sanded. Once it’s primed and painted, the piece reads as one object.',
  },
  {
    question: 'What materials do you use for large 3D prints?',
    answer:
      'Mostly resin on our large-format SLA printers, for detail and a smooth surface, and plastic on our FDM print farm for large runs and tough parts. We pick the process for the size, detail, weight and finish you need, and the quote names it.',
  },
  {
    question: 'Can large 3D prints be painted or plated?',
    answer:
      'Yes. We fill the seams, sand, prime and paint in your colors, or metal plate for chrome, gold or bronze looks. Plating coats the surface; the piece isn’t solid metal.',
  },
  {
    question: 'Do you ship large 3D prints?',
    answer:
      'Yes, to all 50 states, with next-day shipping available once the piece is ready. In New York we can deliver by courier, or you can pick up by appointment in Glendale, Queens or Brooklyn. Packing for big pieces is quoted with the job.',
  },
  {
    question: 'Do I need a 3D file for a large print?',
    answer: `No. Send photos, renders or a sketch and we’ll model it first; 3D modeling starts at $${pricing.modelingFrom}. If you have a file, STL, OBJ, STEP, STP or 3DM all work, and files up to 500 MB upload straight to the quote planner.`,
  },
]

module.exports = {
  page,
  card,
  photos,
  intro,
  glance,
  work,
  before,
  contrast,
  fit,
  pricing: pricingBlock,
  steps,
  checklist,
  faqs,
  schema: {
    name: 'Large Scale 3D Printing Service',
    serviceType: 'Large-format 3D printing',
    description:
      'Large-format 3D printing in New York for sculptures, props, giant product replicas, architectural models and prototypes: prints sectioned and joined to 6 feet and more, then sanded, painted or metal plated, shipped to all 50 states.',
    offer:
      'Small finished props from about $1,000; 12–24 inch hero props $1,500–$3,500; 5-foot showpieces around $10,000; other large builds quoted per project.',
    minPrice: 1000,
  },
}
